'use client';

import React from 'react';

import { useGoogleLogin } from '@react-oauth/google';

import Button from '@/components/Button';

import { logError } from '@/helpers/logger/logUtils';

import { GoogleCircleButton } from './controls/GoogleCircleButton';
import styles from './SignIn.module.scss';

const GOOGLE_USER_API = 'https://www.googleapis.com/oauth2/v3/userinfo';

type GoogleLoginButtonProps = {
  formState: any;
  children?: React.ReactNode;
  onLogin: any;
  isCircleButton?: boolean;
  ariaLabel?: string;
};
type UserInfoResponse = {
  sub: string;
  name: string;
  email: string;
  picture: string;
  given_name: string;
  family_name: string;
  birthday: string;
};

const loadGoogleInfo = async (token: string): Promise<UserInfoResponse> => {
  const response = await fetch(GOOGLE_USER_API, { headers: { Authorization: `Bearer ${token}` } });
  if (response.ok) {
    return response.json();
  }
  throw new Error('Failed to load user info');
};

export const GoogleLoginButton = ({
  formState,
  children,
  onLogin,
  isCircleButton,
  ariaLabel = 'Continue with Google',
}: GoogleLoginButtonProps) => {
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const info = await loadGoogleInfo(tokenResponse.access_token);
        onLogin({
          ...formState,
          socialMediaId: info.sub,
          firstname: info.given_name,
          lastname: info.family_name,
          email: info.email,
          photo: info.picture,
          type: 'google',
        });
      } catch (error: any) {
        logError(`Google login failure: ${error}`);
      }
    },
    onError: () => {
      logError(`Google login failure`);
    },
  });

  if (isCircleButton) {
    return <GoogleCircleButton googleLogin={googleLogin} ariaLabel={ariaLabel} />;
  }

  return (
    <Button className={styles.googleButton} onClick={googleLogin}>
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className={styles.googleButtonIcon}>
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
        <path fill="none" d="M0 0h48v48H0z" />
      </svg>
      <span className={styles.socialButtonText}>{children}</span>
    </Button>
  );
};
